// MST
import { types, flow, cast, detach } from 'mobx-state-tree'

// Interfaces
import {
  ICompanyGetDropdownAttrs,
  ICompanyGetDetailAttrs,
  ICompanyGetListAttrs,
  ICompanyUpdateAttrs,
} from 'interfaces/back-office/master/company/company-view.interface'
import {
  ICompanyApiDropdownResponse,
  ICompanyApiListResponse,
  ICompanyApiDetailResponse,
  ICompanyApiNullResponse,
} from 'interfaces/back-office/master/company/company-api.interface'
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponseDropdownTypesModel, withHelper_paginationProps } from 'models/extensions/with-helper'
import { withHelper_commonLoadingModel, withHelper_commonMapLoading } from 'models/extensions/helper'

// Lodash
import omit from 'lodash/omit'

// Utils
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'

// Constant
import { LOADING } from 'constant/shared.constant'

// Types
const { maybeNull, number, string, boolean, model, optional, array } = types

// Loading
const { BASE, DETAIL, ACTION, DROPDOWN } = LOADING

// Company Props
const companyProps = {
  id: maybeNull(number),
  number: maybeNull(number),
  name: maybeNull(string),
  address: maybeNull(string),
  phoneNumber: maybeNull(string),
  type: maybeNull(string),
  branch: withHelper_baseResponseDropdownTypesModel('BranchDropdown'),
  division: withHelper_baseResponseDropdownTypesModel('DivisionDropdown'),
  status: maybeNull(boolean),
  createdAt: maybeNull(string),
  updatedAt: maybeNull(string),
}

// Company List Model
const CompanyList = optional(array(model('CompanyList').props(omit(companyProps, ['branch', 'division']))), [])

// Company Paginated
const CompanyPaginated = model('CompanyPaginated').props({ ...withHelper_paginationProps, list: CompanyList })

// Company Detail Model
const CompanyDetail = model('CompanyDetail').props(omit(companyProps, ['number']))

//

// Base Model
const CompanyStoreModel = model('Company')
  .props({
    masterCompany_loading: withHelper_commonLoadingModel,
    masterCompany_isSaveLoading: optional(boolean, false),
    masterCompany_detail: optional(CompanyDetail, {}),
    masterCompany_list: optional(CompanyPaginated, {}),
    masterCompany_dropdown: withHelper_baseResponseDropdownTypesModel('CompanyDropdown'),
  })
  .actions(self => ({
    masterCompany_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.masterCompany_loading, payload)

      detach(self.masterCompany_loading)
      self.masterCompany_loading = cast(map)
    },
    masterCompany_SET_LIST: (payload: ICompanyApiListResponse['res']['data']): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map((list, index) => {
          return {
            ...list,
            number: commonUtils_renderTableNumber(index, payload),
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }

      self.masterCompany_list = cast(mapPayload)
    },
    masterCompany_SET_DETAIL: (payload: ICompanyApiDetailResponse['res']['data']): void => {
      self.masterCompany_detail = cast(payload)
    },
    masterCompany_SET_DROPDOWN: (payload: ICompanyApiDropdownResponse['res']['data']): void => {
      self.masterCompany_dropdown = cast(payload.filter(dropdown => dropdown.value))
    },
    masterCompany_CLEAR_LIST: (): void => {
      self.masterCompany_list = cast({})
    },
    masterCompany_CLEAR_DETAIL: (): void => {
      self.masterCompany_detail = cast({})
    },
    masterCompany_CLEAR_DROPDOWN: (): void => {
      self.masterCompany_dropdown = cast([])
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch companies
     *
     * @param {ICompanyGetListAttrs} payload
     *
     * @return {any} any
     */
    masterCompany_fetchList: flow(function* (payload: ICompanyGetListAttrs) {
      self.masterCompany_SET_LOADING({ type: BASE, value: true })

      const response: ICompanyApiListResponse = yield self.environment.masterCompanyApi.masterCompany_getList({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) self.masterCompany_SET_LIST(response.res.data)

      self.masterCompany_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Fetch company detail
     *
     * @param {ICompanyGetDetailAttrs} payload
     *
     * @return {any} any
     */
    masterCompany_fetchDetail: flow(function* (payload: ICompanyGetDetailAttrs) {
      self.masterCompany_SET_LOADING({ type: DETAIL, value: true })

      const response: ICompanyApiDetailResponse = yield self.environment.masterCompanyApi.masterCompany_getDetail({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        self.masterCompany_SET_DETAIL(response.res.data)

        // Do when response success
        if (payload?.onSuccess) {
          payload.onSuccess()
        }
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) {
          payload.onError()
        }
      }

      self.masterCompany_SET_LOADING({ type: DETAIL, value: false })
    }),

    /**
     * @description Fetch company dropdown
     *
     * @param {ICompanyGetDropdownAttrs} payload
     *
     * @return {any} any
     */
    masterCompany_fetchDropdown: flow(function* (payload: ICompanyGetDropdownAttrs) {
      self.masterCompany_SET_LOADING({ type: DROPDOWN, value: true })

      const response: ICompanyApiDropdownResponse = yield self.environment.masterCompanyApi.masterCompany_getDropdown({
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        self.masterCompany_SET_DROPDOWN(response.res.data || [])

        // Do when response success
        if (payload?.onSuccess) {
          payload.onSuccess()
        }
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) {
          payload.onError()
        }
      }

      self.masterCompany_SET_LOADING({ type: DROPDOWN, value: false })
    }),

    /**
     * @description Update company
     *
     * @param {ICompanyUpdateAttrs} payload
     *
     * @return {any} any
     */
    masterCompany_update: flow(function* (payload: ICompanyUpdateAttrs) {
      self.masterCompany_SET_LOADING({ type: ACTION, value: true })

      const response: ICompanyApiNullResponse = yield self.environment.masterCompanyApi.masterCompany_update({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) {
          payload.onError()
        }
      }

      self.masterCompany_SET_LOADING({ type: ACTION, value: false })
    }),
  }))

export const createCompanyStoreDefaultModel = () => optional(CompanyStoreModel, {})
